import { ssoClient } from "@better-auth/sso/client";
import { organizationClient, twoFactorClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import { SignupContextPayload } from "pages/auth/tracking";

import { isValidEmail } from "lib/emailUtils";

// TODO(APP-5958): improve error handling for better-auth endpoints — we currently just surface the
// server's error message verbatim.
export function betterAuthErrorText(
  error: unknown,
  defaultText: string = "Something went wrong",
): string {
  return (error as { message?: string })?.message || defaultText;
}

const configuredUrl = window.authServiceBaseUrl ?? "/api/auth";
const absoluteUrl = new URL(configuredUrl, window.location.origin).href.replace(/\/$/, ""); // strip trailing slash
export const authBaseUrl = absoluteUrl.endsWith("/api/auth")
  ? absoluteUrl
  : `${absoluteUrl}/api/auth`;

/**
 * Shared better-auth client for the login/register flow served under /auth/*.
 *
 * `baseURL` is injected at runtime by Rails (see app/views/shared/_javascript_data.html.erb):
 *   - prod: "" → same-origin "/api/auth/*", reverse-proxied to the auth-service. First-party cookies.
 *   - dev:  e.g. "http://localhost:3015" → cross-origin to the standalone auth-service, which must
 *     trust the webapp origin (BETTER_AUTH_TRUSTED_ORIGINS) and receive credentials (below).
 */
export const authClient = createAuthClient({
  baseURL: authBaseUrl,
  fetchOptions: {
    // Required so session cookies are sent/stored on cross-origin dev requests.
    credentials: "include",
    // Resolve the global fetch per request (better-auth otherwise captures it at creation), so
    // interceptors installed later, like MSW in tests, see the call.
    customFetchImpl: (input, init) => fetch(input, init),
  },

  // TODO(auth-migration): add the `inferAdditionalFields` client plugin so the auth-service's extra
  // fields (firstName / lastName / companyId / teamId) are typed on the client session.
  plugins: [
    organizationClient({
      teams: {
        enabled: true,
      },
    }),
    // TOTP two-factor auth. We deliberately omit `onTwoFactorRedirect` / `twoFactorPage`: each sign-in
    // path routes to the /auth/2fa challenge page (BetterAuthTwoFactorPage) itself. Email/password
    // stays in the SPA, so BetterAuthLoginPage inspects the result and navigates there — see useSignIn.
    // Social sign-in leaves the SPA, so the auth-service redirects enrolled users there after the
    // OAuth callback (see social-two-factor.ts).
    twoFactorClient(),

    // Enterprise SSO (OIDC / SAML)
    ssoClient({
      domainVerification: {
        enabled: true,
      },
    }),
  ],
});

export const { signIn, signUp, signOut, useSession, twoFactor } = authClient;

// Absolute URL for the page better-auth should redirect to after an emailed action (e.g. clicking a
// verify-email link).
export const postAuthRedirect = (path: string) => `${window.location.origin}${path}`;

/**
 * Where better-auth sends the browser after it processes a verification link.
 *
 * One URL serves both outcomes because better-auth's /verify-email takes a single `callbackURL` and
 * appends `?error=<CODE>` on failure — so it has to point at a page that can handle either. That's
 * `/auth/email-confirmed`, which forwards straight into the app on success (the user is signed in by
 * then, via `autoSignInAfterVerification`) and explains itself on failure.
 *
 * `email` rides along so a failed link can offer a resend without asking for it again; `redirect`
 * preserves the destination the user was originally heading for. better-auth keeps our query params
 * and appends its own with `&`.
 */
export const verificationCallbackUrl = ({
  email,
  redirect,
}: {
  email: string;
  redirect?: string;
}): string => {
  const url = new URL(postAuthRedirect("/auth/email-confirmed"));
  url.searchParams.set("email", email);
  if (redirect) url.searchParams.set("redirect", redirect);
  return url.toString();
};

export type SocialProviderType = "google" | "microsoft";

/**
 * better-auth's client resolves with `{ data, error }` instead of rejecting, so an awaited start
 * looks like a success even when the provider was never reached — no provider matched the domain,
 * its domain isn't verified, the service 5xx'd. Re-throw the error object (the same convention as
 * useSignIn) so the caller can report it rather than leave the page sitting there.
 */
function throwIfFailed(result: { error?: unknown }): void {
  if (result?.error) throw result.error;
}

/**
 * Kick off Google / Microsoft SSO. The OAuth flow leaves the SPA and returns to /auth/callback
 * (success or failure), which resolves the session and forwards the user on. We thread the intended
 * `redirect` through the callback URL because a client-held value can't survive the external
 * round-trip; on failure better-auth appends an `?error=…` param that the callback page surfaces.
 *
 * @param signupContext (marketing attribution) is passed as better-auth `additionalData`: it rides the
 * OAuth round-trip in server-side state and the auth-service reads it when creating a new user (see
 * signup-context.ts). This avoids relying on a cookie, which cookie consent makes unreliable.
 *
 * @param loginHint whatever email the user has entered, sent as OAuth `login_hint` so the provider
 * pre-fills its account picker / sign-in form. It's advisory — the user can still pick another
 * account — but anything that isn't a well-formed email is dropped rather than pre-filled.
 */
export async function signInWithProvider(
  provider: SocialProviderType,
  redirect?: string,
  signupContext?: SignupContextPayload,
  loginHint?: string,
) {
  const callback = new URL(postAuthRedirect("/auth/callback"));
  callback.searchParams.set("redirect", redirect ?? "/");
  const hint = loginHint?.trim();
  const validHint = hint && isValidEmail(hint) ? hint : undefined;

  throwIfFailed(
    await authClient.signIn.social({
      provider,
      callbackURL: callback.toString(),
      // Also set to /auth/callback matching the callbackURL just without the redirect param
      // This is because currently the callback page handles both success and failure, on error
      // it adds params automatically to errorCallbackURL
      errorCallbackURL: postAuthRedirect("/auth/callback"),
      ...(validHint ? { loginHint: validHint } : {}),
      ...(signupContext ? { additionalData: { signupContext } } : {}),
    }),
  );
}

/**
 * Kick off enterprise SSO (OIDC / SAML). `providerId` is optional: with none, better-auth resolves a
 * provider from the email's domain, which is what a domain covered by more than one does. The email
 * is only sent as `loginHint` when it's well-formed, matching signInWithProvider.
 */
export async function signInWithEnterpriseSSO(
  email: string,
  redirect?: string,
  providerId?: string,
  signupContext?: SignupContextPayload,
) {
  const callback = new URL(postAuthRedirect("/auth/callback"));
  callback.searchParams.set("redirect", redirect ?? "/");

  throwIfFailed(
    await authClient.signIn.sso({
      email,
      providerId,
      callbackURL: callback.toString(),
      errorCallbackURL: postAuthRedirect("/auth/callback"),
      ...(isValidEmail(email) ? { loginHint: email } : {}),
      ...(signupContext ? { additionalData: { signupContext } } : {}),
    }),
  );
}
